import {
  memo,
  useCallback,
  useContext,
  useState,
  type ChangeEvent,
  type FocusEvent,
  type KeyboardEvent,
  type MouseEvent,
} from 'react'

import { QuantityCounterButtonStyled } from './styles/QuantityCounterButtonStyled'
import { QuantityCounterStyled } from './styles/QuantityCounterStyled'
import { QuantityValueInputStyled } from './styles/QuantityValueInputStyled'
import { QuantityValueStyled } from './styles/QuantityValueStyled'
import { UIContext } from '@ui/core/UIContext'
import { IconAdd, IconDelete, IconMinus } from '@ui/elements/Icon'

const MIN_VALUE = 1
const DEFAULT_DECIMAL_SEPARATOR = '.'

const stripNonDigits = (text: string): string => text.replace(/\D/g, '')

// Anything other than a digit or the locale's decimal separator - a thousands
// separator included - is dropped, matching how NumberField reads a quantity.
const toDraft = (
  typed: string,
  decimalPlaces: number,
  decimalSeparator: string
): string => {
  if (decimalPlaces <= 0) return stripNonDigits(typed)

  const [whole, ...fractions] = typed
    .split(decimalSeparator)
    .map(stripNonDigits)

  return fractions.length > 0
    ? `${whole}${decimalSeparator}${fractions.join('').slice(0, decimalPlaces)}`
    : whole
}

const toQuantity = (draft: string, decimalSeparator: string): number =>
  Number(draft.replace(decimalSeparator, '.'))

export type QuantityCounterChangeEvent =
  | MouseEvent<HTMLButtonElement>
  | KeyboardEvent<HTMLInputElement>
  | FocusEvent<HTMLInputElement>

export interface IQuantityCounterProps {
  /**
   * Current quantity value
   */
  value: number
  /**
   * Callback when quantity changes (increment/decrement)
   */
  onChange: (newValue: number, event: QuantityCounterChangeEvent) => void
  /**
   * Callback when delete action is triggered (when value is at minimum)
   */
  onDelete?: (event: MouseEvent<HTMLButtonElement>) => void
  /**
   * Maximum allowed value (optional)
   */
  max?: number
  /**
   * Disable all interactions
   */
  disabled?: boolean
  /**
   * Disables the remove button independently of `disabled`.
   * Defaults to `disabled` value if not provided.
   */
  isRemoveDisabled?: boolean
  /**
   * Lets the value be typed instead of only stepped. Off by default so the
   * counter keeps the read-only display everywhere it is already used.
   */
  isValueEditable?: boolean
  /**
   * How many decimal places a typed value may carry. At 0 only whole numbers
   * can be typed. Above 0 a typed value may also fall between 0 and 1.
   */
  decimalPlaces?: number
  /**
   * ARIA label for the control
   */
  'aria-label'?: string
  valueInputAriaLabel?: string
  removeButtonAriaLabel?: string
  increaseButtonAriaLabel?: string
  decreaseButtonAriaLabel?: string
  className?: string
}

export const QuantityCounter = memo(
  ({
    value,
    onChange,
    onDelete,
    max,
    disabled = false,
    isRemoveDisabled: isRemoveDisabledProp,
    isValueEditable = false,
    decimalPlaces = 0,
    'aria-label': ariaLabel,
    valueInputAriaLabel,
    removeButtonAriaLabel,
    increaseButtonAriaLabel,
    decreaseButtonAriaLabel,
    className,
  }: IQuantityCounterProps) => {
    const decimalSeparator =
      useContext(UIContext).i18n?.decimalSeparator ?? DEFAULT_DECIMAL_SEPARATOR
    const [draft, setDraft] = useState<Nullable<string>>(null)
    const clampToBounds = useCallback(
      (candidate: number) => {
        const isAboveFloor =
          decimalPlaces > 0 ? candidate > 0 : candidate >= MIN_VALUE
        const atLeastMinimum = isAboveFloor ? candidate : MIN_VALUE

        return max !== undefined
          ? Math.min(atLeastMinimum, max)
          : atLeastMinimum
      },
      [max, decimalPlaces]
    )

    // A typed quantity is the one the person means, so the steppers and the
    // remove affordance read from it rather than from the last committed
    // value. An emptied field, or a zero on the way to a fraction like `0.5`,
    // states no quantity yet, so it keeps deferring to the committed value
    // instead of reading as a request to remove the item.
    const typedQuantity = draft ? toQuantity(draft, decimalSeparator) : 0
    const hasTypedQuantity = typedQuantity > 0
    const currentValue = hasTypedQuantity ? clampToBounds(typedQuantity) : value
    const isAtOrBelowOne = currentValue <= 1
    const isAtMaximum = max !== undefined && currentValue >= max
    const showRemoveButton = isAtOrBelowOne && !!onDelete
    const displayValue =
      draft ?? value.toString().replace('.', decimalSeparator)
    const numDigits = displayValue.length
    const isRemoveDisabled = isRemoveDisabledProp ?? disabled

    // Clicking a button must not blur the field first: the commit that a blur
    // triggers would change the value, and the remove and decrement buttons
    // share a slot, so the click could land on whichever one the re-render
    // put there.
    const handleButtonMouseDown = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => event.preventDefault(),
      []
    )

    const handleDecrement = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => {
        if (!disabled && !isAtOrBelowOne) {
          const newValue = currentValue - 1
          setDraft(null)
          onChange(newValue, event)
        }
      },
      [disabled, isAtOrBelowOne, currentValue, onChange]
    )

    const handleIncrement = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => {
        if (!disabled && !isAtMaximum) {
          const newValue = clampToBounds(currentValue + 1)
          setDraft(null)
          onChange(newValue, event)
        }
      },
      [disabled, isAtMaximum, currentValue, clampToBounds, onChange]
    )

    // Refuse a bad keystroke, or any edit left with nothing valid; clean up a
    // larger edit (paste, autofill). The field and caret are written here
    // because React resets an input only when it differs from state, and that
    // reset moves the caret to the end.
    const handleDraftChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        const input = event.target
        const typed = input.value
        const sanitized = toDraft(typed, decimalPlaces, decimalSeparator)
        const caret = input.selectionStart ?? typed.length

        if (sanitized === typed) {
          setDraft(typed)
          return
        }

        const isTyped =
          'inputType' in event.nativeEvent &&
          event.nativeEvent.inputType === 'insertText'
        const isRefused = isTyped || sanitized === ''
        const accepted = isRefused ? displayValue : sanitized
        const acceptedCaret = Math.max(
          caret - (typed.length - accepted.length),
          0
        )

        input.value = accepted
        input.setSelectionRange(acceptedCaret, acceptedCaret)
        if (!isRefused) setDraft(accepted)
      },
      [decimalPlaces, decimalSeparator, displayValue]
    )

    const commitDraft = useCallback(
      (
        event: KeyboardEvent<HTMLInputElement> | FocusEvent<HTMLInputElement>
      ) => {
        if (draft === null) return

        const committed = clampToBounds(toQuantity(draft, decimalSeparator))

        setDraft(null)
        if (committed !== value) onChange(committed, event)
      },
      [draft, clampToBounds, decimalSeparator, onChange, value]
    )

    const handleInputKeyDown = useCallback(
      (event: KeyboardEvent<HTMLInputElement>) => {
        // Escape abandons the draft, so the field shows the quantity the
        // consumer still holds. The blur that follows finds no draft to commit.
        if (event.key === 'Escape') {
          setDraft(null)
          return
        }

        if (event.key !== 'Enter') return
        event.preventDefault()
        commitDraft(event)
      },
      [commitDraft]
    )

    const handleDelete = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => {
        if (!isRemoveDisabled) {
          onDelete(event)
        }
      },
      [isRemoveDisabled, onDelete]
    )

    return (
      <QuantityCounterStyled
        className={className}
        role='group'
        aria-label={ariaLabel || 'Quantity counter'}
        $isValueEditable={isValueEditable}
      >
        {showRemoveButton ? (
          <QuantityCounterButtonStyled
            onClick={handleDelete}
            onMouseDown={handleButtonMouseDown}
            disabled={isRemoveDisabled}
            aria-label={removeButtonAriaLabel || 'Remove item'}
          >
            <IconDelete />
          </QuantityCounterButtonStyled>
        ) : (
          <QuantityCounterButtonStyled
            onClick={handleDecrement}
            onMouseDown={handleButtonMouseDown}
            disabled={disabled || isAtOrBelowOne}
            aria-label={decreaseButtonAriaLabel || 'Decrease quantity'}
          >
            <IconMinus />
          </QuantityCounterButtonStyled>
        )}
        {isValueEditable ? (
          <QuantityValueInputStyled
            type='text'
            inputMode={decimalPlaces > 0 ? 'decimal' : 'numeric'}
            numDigits={numDigits}
            value={displayValue}
            placeholder={MIN_VALUE.toString()}
            disabled={disabled}
            aria-label={valueInputAriaLabel || 'Quantity'}
            onChange={handleDraftChange}
            onKeyDown={handleInputKeyDown}
            onBlur={commitDraft}
          />
        ) : (
          <QuantityValueStyled numDigits={numDigits}>
            {value}
          </QuantityValueStyled>
        )}
        <QuantityCounterButtonStyled
          onClick={handleIncrement}
          onMouseDown={handleButtonMouseDown}
          disabled={disabled || isAtMaximum}
          aria-label={increaseButtonAriaLabel || 'Increase quantity'}
        >
          <IconAdd />
        </QuantityCounterButtonStyled>
      </QuantityCounterStyled>
    )
  }
)

QuantityCounter.displayName = 'QuantityCounter'
